import React from 'react';
import { useStyles } from './FiltersForm.styles';
import { Field, FieldProps, Form, FormikProps, useFormikContext } from 'formik';
import Switch from '@mui/material/Switch';
import { UserInfo } from '@shared/interfaces/UserInfo';
import { Autocomplete, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import { projectEndObject, statusObject, userStatusesObject } from '../../constants/constants';
import { PeopleSearchSelector } from '@shared/components/PeopleSearchSelector/PeopleSearchSelector';
import { FilterFields } from '../../enums/FilterFields';
import { PeopleSelector } from '@shared/components/PeopleSelector/PeopleSelector';
import { FiltersRadioButtons } from '../FiltersRadioButtons/FiltersRadioButtons';
import { FiltersMultipleAutocomplete } from '../FiltersMultipleAutocomplete/FiltersMultipleAutocomplete';
import { FlagFiltersFields } from '../../interfaces/FlagFiltersFields';
import { DepartmentData, DepartmentStatus } from '@shared/interfaces/DepartmentData';
import { useHandlers } from '../../hooks/useHandlers';
import { rockySwitcherStyles } from '@styles/rockySwitcherStyles';
import { useAllFlagsData } from '../../hooks/useAllFlagsData';
import { DateRangeFilter } from '@shared/components/DateRangeFilter/DateRangeFilter';
import { LocationSelector } from '@shared/components/LocationSelector/LocationSelector';
import { DepartmentSelector } from '@shared/components/DepartmentSelector/DepartmentSelector';

interface Props {
  allDeparttmentsData: DepartmentData[];
  tomData: UserInfo[];
  prData: UserInfo[];
}

export const FiltersForm: React.FC<Props> = ({ allDeparttmentsData, tomData, prData }) => {
  const styles = useStyles();
  const rockySwitcher = rockySwitcherStyles();
  const formik: FormikProps<FlagFiltersFields> = useFormikContext();
  const { locationOptions: locations } = useAllFlagsData();
  const {
    handleNewHomeChange,
    handleChangeRadioBtn,
    handleStatusChange,
    handleLocationChange,
    handleProjectsChange,
    handlePeopleSelectorChange,
    handleUserStatusesChange,
    handleReasonsSubReasonsChange,
    handleDatesChange,
    handleIsEscalatedByClientChange,
    handleIsAnotherOfferInvolvedChange,
    reasonsSubReasonsOptions,
  } = useHandlers(allDeparttmentsData);

  const selectedReasonsSubReasonsOptions = reasonsSubReasonsOptions.filter((option) =>
    formik.values.reasonsSubReasons.some((x) => x === option.value)
  );
  const selectedLocations = (locations ?? []).filter((option) =>
    formik.values.legalLocations.some((x) => x === option.value)
  );

  const activeDepartments = allDeparttmentsData.filter((dept) => dept.status === DepartmentStatus.Active);

  return (
    <Form>
      <Grid container spacing={2} className={styles.drawlerRoot}>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Severity and status</Typography>
          <FiltersRadioButtons row name={FilterFields.Severity} handleChange={handleChangeRadioBtn} severity />
        </Grid>
        <Grid item xs={12}>
          <FiltersMultipleAutocomplete
            name={FilterFields.Status}
            value={formik.values.status}
            handleChange={handleStatusChange}
            options={Object.values(statusObject).reduce((acc, item) => {
              acc.push(item.label);
              return acc;
            }, [] as string[])}
            label="Status"
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            sx={{
              '& .MuiAutocomplete-inputRoot': {
                padding: '10px',
              },
            }}
            multiple
            filterOptions={(x, state) => x.filter((option) => option.label.toLowerCase().includes(state.inputValue))}
            getOptionLabel={(option) => option.label}
            groupBy={(option) => option.mainFlagReason}
            noOptionsText="No such reasons"
            options={reasonsSubReasonsOptions}
            value={selectedReasonsSubReasonsOptions}
            onChange={(_, value) => handleReasonsSubReasonsChange(FilterFields.ReasonsSubReasons, value)}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Reasons"
                placeholder={selectedReasonsSubReasonsOptions.length === 0 ? 'All reasons' : undefined}
              />
            )}
            renderOption={(props, option) => {
              return (
                <li
                  {...props}
                  style={{
                    paddingLeft: '24px',
                    paddingTop: '6px',
                    paddingBottom: '6px',
                  }}
                >
                  {option.isPrimaryReason ? (
                    option.label
                  ) : (
                    <>
                      <Typography variant="body2" fontSize="22px" sx={{ marginRight: '10px' }}>
                        ∙
                      </Typography>
                      {option.label}
                    </>
                  )}
                </li>
              );
            }}
          />
        </Grid>
        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Field
            name={FilterFields.IsEscalatedByClient}
            type="checkbox"
            component={({ field }: FieldProps) => (
              <FormControlLabel
                className={styles.formControlLabel}
                control={
                  <Switch
                    {...field}
                    className={rockySwitcher.switcher}
                    checked={formik.values.isEscalatedByClient}
                    onChange={(e) => {
                      handleIsEscalatedByClientChange(FilterFields.IsEscalatedByClient, e.target.checked);
                    }}
                  />
                }
                label={<Typography variant="subtitle1">Issue brought by client</Typography>}
              />
            )}
          />
          <Field
            name={FilterFields.isAnotherOfferInvolved}
            onChange={formik.handleChange}
            type="checkbox"
            component={({ field }: FieldProps) => (
              <FormControlLabel
                className={styles.formControlLabel}
                control={
                  <Switch
                    {...field}
                    className={rockySwitcher.switcher}
                    checked={formik.values.isAnotherOfferInvolved}
                    onChange={(e) => {
                      handleIsAnotherOfferInvolvedChange(FilterFields.isAnotherOfferInvolved, e.target.checked);
                    }}
                  />
                }
                label={<Typography variant="subtitle1">Another Offer Involved</Typography>}
              />
            )}
          />
          <Field
            name={FilterFields.IsNewHomeNeeded}
            onChange={formik.handleChange}
            type="checkbox"
            component={({ field }: FieldProps) => (
              <FormControlLabel
                className={styles.formControlLabel}
                control={
                  <Switch
                    {...field}
                    className={rockySwitcher.switcher}
                    checked={formik.values.isNewHomeNeeded}
                    onChange={handleNewHomeChange}
                  />
                }
                label={<Typography variant="subtitle1">NewHome needed</Typography>}
              />
            )}
          />
        </Grid>
        {formik.values.isNewHomeNeeded && (
          <Grid item xs={12}>
            <Typography variant="body2" className={styles.radioBtnTitle}>
              Project end date
            </Typography>
            <FiltersRadioButtons
              name={FilterFields.ProjectEnd}
              handleChange={handleChangeRadioBtn}
              labelValueObject={projectEndObject}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography variant="subtitle1" className={styles.chapterTitle}>
            Dates
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <DateRangeFilter
            label="Escalation date"
            fromField={FilterFields.EscalationDateFrom}
            toField={FilterFields.EscalationDateTo}
            fromValue={formik.values.escalationDateFrom}
            toValue={formik.values.escalationDateTo}
            onChange={handleDatesChange}
          />
        </Grid>
        <Grid item xs={12}>
          <DateRangeFilter
            label="Resolved date"
            fromField={FilterFields.ResolvedDateFrom}
            toField={FilterFields.ResolvedDateTo}
            fromValue={formik.values.resolvedDateFrom}
            toValue={formik.values.resolvedDateTo}
            onChange={handleDatesChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" className={styles.chapterTitle}>
            Who's involved
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <FiltersMultipleAutocomplete
            name={FilterFields.UserStatuses}
            value={formik.values.userStatuses}
            handleChange={handleUserStatusesChange}
            options={Object.values(userStatusesObject).reduce((acc, item) => {
              acc.push(item.label);
              return acc;
            }, [] as string[])}
            label="Engagement status"
          />
        </Grid>
        <Grid item xs={12}>
          <PeopleSearchSelector
            title="Flags raised for"
            name={FilterFields.InvolvedUsers}
            onChange={handlePeopleSelectorChange}
            value={formik.values.involvedUsers}
            includeInactive
          />
        </Grid>
        <Grid item xs={12}>
          <PeopleSearchSelector
            title="Manager"
            name={FilterFields.FlagManagers}
            onChange={handlePeopleSelectorChange}
            value={formik.values.flagManagers}
          />
        </Grid>
        <Grid item xs={12}>
          <PeopleSearchSelector
            title="Author"
            name={FilterFields.FlagAuthors}
            onChange={handlePeopleSelectorChange}
            value={formik.values.flagAuthors}
          />
        </Grid>
        {formik.values.isNewHomeNeeded && (
          <Grid item xs={12}>
            <PeopleSelector
              data={tomData}
              title="Assigned TOM"
              name={FilterFields.AssignedTomIds}
              onChange={handlePeopleSelectorChange}
              value={formik.values.assignedTomIds}
              tom
            />
          </Grid>
        )}
        {/* we would require this feature in future */}
        <Grid item xs={12} display={'none'}>
          <PeopleSelector
            data={prData}
            title="Assigned HR"
            name={FilterFields.AssignedPrIds}
            onChange={handlePeopleSelectorChange}
            value={formik.values.assignedPrIds}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" className={styles.chapterTitle}>
            Location
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <LocationSelector
            name={FilterFields.LegalLocations}
            options={locations ?? []}
            value={selectedLocations}
            onChange={handleLocationChange}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" className={styles.chapterTitle}>
            Projects
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <DepartmentSelector
            options={activeDepartments ?? []}
            onChange={handleProjectsChange}
            name={FilterFields.AssignedProjects}
            value={formik.values.assignedProjects}
            label="Assigned projects"
          />
        </Grid>
        <Grid item xs={12}>
          <DepartmentSelector
            options={allDeparttmentsData ?? []}
            onChange={handleProjectsChange}
            name={FilterFields.AffectedProjects}
            value={formik.values.affectedProjects}
            label="Affected projects"
          />
        </Grid>
      </Grid>
    </Form>
  );
};
